/**
 * The arena, and everything around it.
 *
 * The canvas is `game/bitka.ts` and draws the battle; this file is the four moments that are words
 * rather than pixels — agreeing the terms, handing in a link, casting a vote, and the revision the
 * whole feature exists to produce.
 *
 * There is no navigation item for any of it. A duel is reached from a person's card, from the SADA
 * card when it has a deadline on it, and from the crew screen when it wants a vote. When there is
 * no duel there is no trace of one anywhere, which is the same promise the rest of the product
 * makes about every screen a member has not earned yet.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { OSA_LABELS, copy } from '../../core/copy';
import {
  OSE,
  ROKOVI_GLASANJA,
  ROKOVI_RADA,
  axesWonBy,
  deadlineNow,
  ishodOf,
  nameOfSide,
  other,
  reviseRefusal,
  sideOf,
  stageOf,
  submitRefusal,
  submittedAt,
  turnOf,
  voteClosesAt,
  waitingDuels,
  type BlindLabel,
  type Duel,
  type Osa,
  type Side,
} from '../../core/dvoboj';
import { checkLink } from '../../core/links';
import { untilWhen } from '../../core/time';
import type { ChallengeOffer } from '../api';
import { api } from '../api';
import { sfx } from '../audio';
import { Btn, Modal, Win } from '../components';
import { Arena } from '../game/arena';
import { Bitka, shortLeft, type BitkaState, type Command } from '../game/bitka';
import {
  ackDuel,
  answerDuel,
  challenge,
  closeDuel,
  contentOf,
  duelPath,
  duelsOf,
  forfeitDuel,
  go,
  loadDuel,
  openDuel,
  reviseDuel,
  submitDuel,
  toast,
  useNow,
  useStore,
  voteDuel,
} from '../store';

/* ================================================================ the arena screen */

export function Dvoboj() {
  const view = useStore((s) => s.duelView);
  const id = useStore((s) => s.openDuel);
  const me = useStore((s) => s.server?.member.id ?? null);
  const now = useNow();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bitkaRef = useRef<Bitka | null>(null);
  const [panel, setPanel] = useState<'none' | 'predaja' | 'zadatak' | 'protivnik'>('none');

  const state: BitkaState | null = useMemo(() => {
    if (!view?.duel || !me) return null;
    const { duel, faces } = view;
    const face = (memberId: string, name: string) => ({
      name,
      look: faces[memberId]?.look ?? 0,
      souvenirs: faces[memberId]?.souvenirs ?? [],
      island: faces[memberId]?.island ?? null,
    });
    return {
      duel,
      tally: view.tally,
      fighters: [face(duel.fromId, duel.fromName), face(duel.toId, duel.toName)],
      me: sideOf(duel, me),
      brief: view.brief?.title ?? duel.nodeId,
      now,
    };
  }, [view, me, now]);

  /* The engine is constructed once and fed; it is never remounted on a state change. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !state) return undefined;
    const bitka = new Bitka(canvas, state, {
      onCommand: (command: Command) => {
        sfx.confirm();
        if (command === 'izadji') {
          closeDuel();
          go('sada');
        } else if (command === 'predaj') setPanel('predaja');
        else if (command === 'zadatak') setPanel('zadatak');
        else setPanel('protivnik');
      },
    });
    bitkaRef.current = bitka;
    bitka.start();

    const observer = new ResizeObserver(() => bitka.resize());
    observer.observe(canvas);
    const onKey = (event: KeyboardEvent) => {
      if (bitka.key(event.key)) event.preventDefault();
    };
    canvas.addEventListener('keydown', onKey);
    return () => {
      bitka.stop();
      bitkaRef.current = null;
      observer.disconnect();
      canvas.removeEventListener('keydown', onKey);
    };
    // Rebuilt only when the duel changes or the first state arrives. Everything after that is fed
    // in by the effect below — the engine is never remounted to show a new number.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, state !== null]);

  useEffect(() => {
    if (state) bitkaRef.current?.refresh(state);
  }, [state]);

  /**
   * The arena socket. A vote landing while somebody is watching gets to be a move; everything else
   * is a refetch, because D1 is the truth and the socket is only ever a nudge.
   */
  useEffect(() => {
    if (!id) return undefined;
    const arena = new Arena(window.location.origin, {
      onEvent: (ev, side, axis) => {
        if (ev === 'glas' && side !== undefined && axis && (OSE as readonly string[]).includes(axis))
          bitkaRef.current?.hit(side, axis as Osa);
        void loadDuel(id);
      },
      onPresence: (name, here) => {
        // An event, said once, and then gone. Never a dot that stays lit — `docs/UZIVO.md` §3 is
        // about not building a loneliness meter, and a duel of two people is the easiest place to
        // accidentally build one.
        if (here && name) bitkaRef.current?.announce(copy.dvoboj.watching(name));
      },
    });
    arena.enter(id);
    return () => arena.leave();
  }, [id]);

  /**
   * A deadline that is running has to be redrawn even when nothing arrives, and a duel that has
   * just run out of time has to notice. One poll a minute; the canvas animates from `elapsed` and
   * needs nothing from here.
   */
  useEffect(() => {
    if (!id) return undefined;
    const timer = window.setInterval(() => void loadDuel(id), 60_000);
    return () => window.clearInterval(timer);
  }, [id]);

  useEffect(() => {
    if (view?.duel && !view.duel.seenAt) void ackDuel(view.duel.id);
  }, [view?.duel]);

  /*
   * A voter, while the vote is open, is shown the two works and nothing else.
   *
   * The arena cannot be drawn for them without putting a name and a per-side bar on the screen,
   * and a name next to a bar that moves when you vote is the mapping the blindness exists to
   * withhold. It comes back the moment the duel resolves.
   */
  if (view?.blind) {
    return (
      <div className="pad">
        <Glasacnica duel={view.duel} view={view} />
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Btn variant="ghost" size="small" onClick={() => { closeDuel(); go('posada'); }}>
            {copy.dvoboj.back}
          </Btn>
        </div>
      </div>
    );
  }

  if (!view?.duel || !state || !me) {
    return (
      <div className="pad">
        <Win title={copy.dvoboj.title}>
          <p className="muted">{copy.error.genericBody}</p>
          <div className="btn-row" style={{ marginTop: 12 }}>
            <Btn variant="ghost" size="small" onClick={() => { closeDuel(); go('sada'); }}>
              {copy.island.back}
            </Btn>
          </div>
        </Win>
      </div>
    );
  }

  const { duel } = view;
  const side = sideOf(duel, me);
  const stage = stageOf(duel, now);

  return (
    <div className="bitka-wrap">
      <canvas
        ref={canvasRef}
        className="bitka-canvas"
        role="img"
        aria-label={copy.a11y.duel}
        tabIndex={0}
        onClick={(event) => bitkaRef.current?.pointAt(event.clientX, event.clientY)}
      />

      {/*
        The same four commands as real buttons, for a screen reader and for a keyboard that never
        reaches the canvas. The canvas draws them; these are the ones that are actually announced.
      */}
      {stage === 'rad' && side !== null ? (
        <div className="visually-hidden">
          <button type="button" onClick={() => setPanel('predaja')}>{copy.dvoboj.menuSubmit}</button>
          <button type="button" onClick={() => setPanel('zadatak')}>{copy.dvoboj.menuBrief}</button>
          <button type="button" onClick={() => setPanel('protivnik')}>{copy.dvoboj.menuOpponent}</button>
          <button type="button" onClick={() => { closeDuel(); go('sada'); }}>{copy.dvoboj.menuLeave}</button>
        </div>
      ) : null}

      <div className="bitka-below">
        {stage === 'poziv' || stage === 'izmena' ? (
          <Pregovori duel={duel} me={me} brief={view.brief?.title ?? duel.nodeId} />
        ) : stage === 'glasanje' ? (
          side === null ? <Glasacnica duel={duel} view={view} /> : <CekaSeGlasanje duel={duel} brief={view.brief?.title ?? duel.nodeId} />
        ) : stage === 'ishod' ? (
          <Ishod duel={duel} view={view} me={me} />
        ) : null}

        <div className="btn-row" style={{ marginTop: 12 }}>
          <Btn variant="ghost" size="small" onClick={() => { closeDuel(); go('sada'); }}>
            {copy.island.back}
          </Btn>
        </div>
      </div>

      {panel === 'predaja' ? (
        <Predaja duel={duel} onClose={() => setPanel('none')} />
      ) : panel === 'zadatak' ? (
        <Modal title={copy.dvoboj.briefLabel} onClose={() => setPanel('none')}>
          <p style={{ fontSize: 14 }}>{view.brief?.title}</p>
          {view.brief?.task ? <p className="muted" style={{ marginTop: 8 }}>{view.brief.task}</p> : null}
        </Modal>
      ) : panel === 'protivnik' ? (
        <Protivnik duel={duel} me={me} onClose={() => setPanel('none')} />
      ) : null}
    </div>
  );
}

/* ================================================================ agreeing the terms */

function Pregovori({ duel, me, brief }: { duel: Duel; me: string; brief: string }) {
  const now = useNow();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [counter, setCounter] = useState(false);
  const mine = turnOf(duel) === me;
  const them = nameOfSide(duel, other(sideOf(duel, me) ?? 0));
  const deadline = deadlineNow(duel, now);

  const answer = async (action: 'prihvati' | 'odbij' | 'izmena', extra?: Record<string, number | string>) => {
    setBusy(true);
    setError(null);
    const result = await answerDuel(duel.id, { action, ...extra } as Parameters<typeof answerDuel>[1]);
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message ?? null);
      return;
    }
    if (action === 'prihvati') {
      sfx.izazov();
      toast(copy.dvoboj.started, copy.dvoboj.briefIs(brief));
    }
    if (action === 'odbij') {
      closeDuel();
      go('sada');
      toast(copy.dvoboj.declined, copy.dvoboj.declinedBody);
    }
  };

  if (counter)
    return (
      <Win title={copy.dvoboj.counter}>
        <Uslovi
          initial={{ nodeId: duel.nodeId, workHours: duel.workHours, voteHours: duel.voteHours }}
          briefs={null}
          themName={them}
          busy={busy}
          submitLabel={copy.dvoboj.counter}
          onCancel={() => setCounter(false)}
          onSubmit={(terms) => void answer('izmena', terms)}
        />
        {error ? <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>{error}</p> : null}
      </Win>
    );

  return (
    <Win title={copy.dvoboj.title} tone={mine ? 'gold' : undefined}>
      <p style={{ fontSize: 14 }}>{mine ? copy.dvoboj.incomingFrom(them) : copy.dvoboj.waitingOnThem(them)}</p>
      <p className="tiny muted" style={{ marginTop: 6 }}>
        {copy.dvoboj.terms(brief, copy.dvoboj.rok(duel.workHours), copy.dvoboj.rok(duel.voteHours))}
      </p>
      {deadline ? (
        <p className="tiny muted" style={{ marginTop: 4 }}>
          {copy.dvoboj.expiresIn(untilWhen(deadline, now))}
        </p>
      ) : null}
      {mine ? (
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Btn variant="primary" size="small" disabled={busy} onClick={() => void answer('prihvati')}>
            {copy.dvoboj.accept}
          </Btn>
          <Btn size="small" disabled={busy} onClick={() => setCounter(true)}>
            {copy.dvoboj.counter}
          </Btn>
          <Btn variant="ghost" size="small" disabled={busy} onClick={() => void answer('odbij')}>
            {copy.dvoboj.decline}
          </Btn>
        </div>
      ) : null}
      {error ? <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>{error}</p> : null}
    </Win>
  );
}

/* ================================================================ handing in the link */

function Predaja({ duel, onClose }: { duel: Duel; onClose: () => void }) {
  const me = useStore((s) => s.server?.member.id ?? '');
  const now = useNow();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refusal = submitRefusal(duel, me, now);

  const send = async () => {
    setBusy(true);
    setError(null);
    const result = await submitDuel(duel.id, url.trim());
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message ?? null);
      return;
    }
    toast(copy.dvoboj.submitted, copy.dvoboj.waitingForTheirs);
    onClose();
  };

  const check = checkLink(url);
  const offCommunity = url.trim().length > 6 && check.ok && !check.community;

  return (
    <Modal title={copy.dvoboj.submitTitle} onClose={onClose}>
      {refusal ? (
        <p className="muted">{copy.dvoboj.refusals[refusal]}</p>
      ) : (
        <>
          <p className="tiny muted">{copy.dvoboj.submitHint}</p>
          <input
            className="field"
            style={{ marginTop: 10 }}
            type="url"
            inputMode="url"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={copy.node.linkPlaceholder}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            disabled={busy}
          />
          {offCommunity ? (
            <p className="tiny" style={{ marginTop: 6, color: 'var(--gold-dark)' }}>
              {copy.submit.notSkool}
            </p>
          ) : null}
          {error ? <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>{error}</p> : null}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <Btn variant="primary" size="small" disabled={!url.trim() || busy} onClick={() => void send()}>
              {copy.dvoboj.submit}
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

/* ================================================================ the opponent, and the way out */

function Protivnik({ duel, me, onClose }: { duel: Duel; me: string; onClose: () => void }) {
  const side = sideOf(duel, me) ?? 0;
  const them = nameOfSide(duel, other(side));
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <Modal title={them} onClose={onClose}>
      <p className="tiny muted">{copy.dvoboj.lead}</p>
      {/*
        Forfeiting lives here rather than on the front panel. RUN is a button you press by accident,
        and this one ends something two people agreed to.
      */}
      <div style={{ marginTop: 16, borderTop: '2px solid var(--paper-edge)', paddingTop: 12 }}>
        {confirming ? (
          <>
            <p className="tiny">{copy.dvoboj.forfeitConfirm}</p>
            <div className="btn-row" style={{ marginTop: 10 }}>
              <Btn
                size="small"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  const result = await forfeitDuel(duel.id);
                  setBusy(false);
                  if (result.ok) {
                    toast(copy.dvoboj.forfeited);
                    onClose();
                    closeDuel();
                    go('sada');
                  }
                }}
              >
                {copy.dvoboj.forfeit}
              </Btn>
              <Btn variant="ghost" size="small" onClick={() => setConfirming(false)}>
                {copy.dvoboj.back}
              </Btn>
            </div>
          </>
        ) : (
          <Btn variant="ghost" size="small" onClick={() => setConfirming(true)}>
            {copy.dvoboj.forfeit}
          </Btn>
        )}
      </div>
    </Modal>
  );
}

/* ================================================================ the vote */

function CekaSeGlasanje({ duel, brief }: { duel: Duel; brief: string }) {
  const now = useNow();
  const closes = voteClosesAt(duel);
  const [copied, setCopied] = useState(false);
  const post = copy.dvoboj.postDraft(
    brief,
    // A real path, not a fragment. `app.tsx` routes on the pathname, so `#dvoboj:` landed every
    // voter on SADA with no way to reach the duel — which makes the post that recruits them a
    // megaphone pointed at the wrong room.
    `${window.location.origin}${duelPath(duel.id)}`,
    closes ? untilWhen(closes, now) : '',
  );

  return (
    <Win title={copy.dvoboj.voting}>
      <p style={{ fontSize: 14 }}>{copy.dvoboj.bothIn}</p>
      {closes ? (
        <p className="tiny muted" style={{ marginTop: 4 }}>
          {copy.dvoboj.votingLeft(untilWhen(closes, now))}
        </p>
      ) : null}

      {/*
        Skool has no API (GDD §8.1), so the poll cannot be read and the verdict cannot live there —
        and it could not be blind there either, because the person who posts it is one of the two.
        The vote is here; the post is what brings the people who cast it.
      */}
      <p className="tiny muted" style={{ marginTop: 14 }}>{copy.dvoboj.postHint}</p>
      <pre className="post-draft">{post}</pre>
      <div className="btn-row" style={{ marginTop: 10 }}>
        <Btn
          size="small"
          onClick={() => {
            void navigator.clipboard?.writeText(post).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
        >
          {copied ? copy.dvoboj.postCopied : copy.dvoboj.postCopy}
        </Btn>
      </div>
    </Win>
  );
}

/** The blind vote. Two works, no names, and one question about the craft. */
export function Glasacnica({ duel, view }: { duel: Duel; view: { works: Array<{ label: BlindLabel; url: string }>; myVote: { label: BlindLabel; axis: Osa } | null } }) {
  const [label, setLabel] = useState<BlindLabel | null>(view.myVote?.label ?? null);
  const [axis, setAxis] = useState<Osa | null>(view.myVote?.axis ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(Boolean(view.myVote));

  const send = async () => {
    if (!label || !axis) return;
    setBusy(true);
    setError(null);
    const result = await voteDuel(duel.id, label, axis);
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message ?? null);
      return;
    }
    setDone(true);
    toast(copy.dvoboj.voteCast, copy.dvoboj.voteChange);
  };

  return (
    <Win title={copy.dvoboj.voting}>
      <p className="tiny muted">{copy.dvoboj.voteBlind}</p>

      <div className="glasovi" style={{ marginTop: 12 }}>
        {view.works.map((work) => (
          <div key={work.label} className={`glas-rad ${label === work.label ? 'chosen' : ''}`.trim()}>
            <b>{copy.dvoboj.voteFor(work.label)}</b>
            <a href={work.url} target="_blank" rel="noreferrer noopener" className="tiny">
              {copy.dvoboj.voteOpen}
            </a>
            <Btn
              size="small"
              variant={label === work.label ? 'primary' : undefined}
              onClick={() => setLabel(work.label)}
            >
              {label === work.label ? copy.dvoboj.voteChosen : copy.dvoboj.voteChoose}
            </Btn>
          </div>
        ))}
      </div>

      {/*
        The second tap, and the reason this is a critique rather than a rating. A vote here cannot
        say „this person is better", only „this beat that, on rhythm" — which is the design's whole
        answer to GDD §10.1, and the thing the loser can actually act on.
      */}
      <p style={{ marginTop: 16, fontSize: 14 }}>{copy.dvoboj.votePrompt}</p>
      <div className="ose" style={{ marginTop: 8 }}>
        {OSE.map((o) => (
          <Btn
            key={o}
            size="small"
            variant={axis === o ? 'gold' : undefined}
            onClick={() => setAxis(o)}
          >
            {OSA_LABELS[o]}
          </Btn>
        ))}
      </div>

      {error ? <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>{error}</p> : null}
      <div className="btn-row" style={{ marginTop: 14 }}>
        <Btn variant="primary" size="small" disabled={!label || !axis || busy} onClick={() => void send()}>
          {copy.dvoboj.voteSubmit}
        </Btn>
        {done ? <span className="tiny muted">{copy.dvoboj.voteChange}</span> : null}
      </div>
    </Win>
  );
}

/* ================================================================ the ending */

function Ishod({ duel, view, me }: { duel: Duel; view: { tally: import('../../core/dvoboj').Tally }; me: string }) {
  const now = useNow();
  const side = sideOf(duel, me);
  const ishod = ishodOf(duel, view.tally, now);
  const refusal = side === null ? 'nije-tvoj' : reviseRefusal(duel, me, now);

  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    const result = await reviseDuel(duel.id, url.trim());
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message ?? null);
      return;
    }
    toast(copy.dvoboj.revised, copy.dvoboj.revisedBody, 'gold');
  };

  const won = side !== null && (ishod.kind === 'pobeda' || ishod.kind === 'predaja') && ishod.winner === side;
  const axes = side === null ? [] : axesWonBy(view.tally, side);

  return (
    <>
      <Win title={copy.dvoboj.title} tone={won ? 'gold' : undefined}>
        {ishod.kind === 'nista' ? (
          <>
            <p style={{ fontSize: 14 }}>{copy.dvoboj.nothing}</p>
            <p className="tiny muted" style={{ marginTop: 6 }}>{copy.dvoboj.nothingBody}</p>
          </>
        ) : ishod.kind === 'nereseno' ? (
          <>
            <p style={{ fontSize: 14 }}>
              {ishod.reason === 'malo-glasova' ? copy.dvoboj.drawFew : copy.dvoboj.drawTied}
            </p>
            {ishod.reason === 'malo-glasova' ? (
              <p className="tiny muted" style={{ marginTop: 6 }}>{copy.dvoboj.drawFewBody}</p>
            ) : null}
          </>
        ) : ishod.kind === 'predaja' ? (
          <>
            <p style={{ fontSize: 14 }}>{copy.dvoboj.walkover}</p>
            {side !== null && submittedAt(duel, side) !== null ? (
              <p className="tiny muted" style={{ marginTop: 6 }}>{copy.dvoboj.walkoverBody}</p>
            ) : null}
          </>
        ) : (
          <>
            <p style={{ fontSize: 16 }}>{side !== null && won ? copy.dvoboj.won : copy.dvoboj.lost}</p>
            {ishod.decisive ? (
              <p className="tiny" style={{ marginTop: 6, color: 'var(--gold-dark)' }}>
                {copy.dvoboj.decided[ishod.decisive]}
              </p>
            ) : null}
            {/*
              The sentence the loser came for. „You were better on colour and idea" is the only
              output of this whole machine that somebody can take back into a project.
            */}
            {axes.length > 0 ? (
              <p className="tiny" style={{ marginTop: 8 }}>
                {(won ? copy.dvoboj.youWon : copy.dvoboj.youLostBut)(
                  axes.map((o) => OSA_LABELS[o]).join(', '),
                )}
              </p>
            ) : null}
          </>
        )}
      </Win>

      {/* The ending, and the same button on both sides. GDD §2③ said this before the duel existed. */}
      {refusal === null ? (
        <Win title={copy.dvoboj.reviseTitle} tone="gold" className="revision-card">
          <p style={{ fontSize: 14 }}>{won ? copy.dvoboj.reviseWhyWon : copy.dvoboj.reviseWhy}</p>
          <p className="tiny muted" style={{ marginTop: 6 }}>{copy.dvoboj.reviseHint}</p>
          <input
            className="field"
            style={{ marginTop: 10 }}
            type="url"
            inputMode="url"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={copy.node.linkPlaceholder}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            disabled={busy}
          />
          {error ? <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>{error}</p> : null}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <Btn variant="gold" size="small" disabled={!url.trim() || busy} onClick={() => void send()}>
              {copy.dvoboj.revise}
            </Btn>
          </div>
        </Win>
      ) : refusal === 'vec-si' ? (
        <p className="tiny" style={{ marginTop: 12, color: 'var(--green-dark)' }}>{copy.dvoboj.revised}</p>
      ) : null}
    </>
  );
}

/* ================================================================ offering one */

/** The terms: a brief and two deadlines, all of them taps. */
function Uslovi({
  initial,
  briefs,
  themName,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: { nodeId: string; workHours: number; voteHours: number };
  briefs: ChallengeOffer['briefs'] | null;
  themName: string;
  busy: boolean;
  submitLabel: string;
  onSubmit: (terms: { nodeId: string; workHours: number; voteHours: number }) => void;
  onCancel: () => void;
}) {
  const [nodeId, setNodeId] = useState(initial.nodeId);
  const [workHours, setWorkHours] = useState(initial.workHours);
  const [voteHours, setVoteHours] = useState(initial.voteHours);
  const chosen = briefs?.find((brief) => brief.id === nodeId) ?? null;

  return (
    <>
      {briefs ? (
        <>
          <p className="eyebrow">{copy.dvoboj.briefLabel}</p>
          <p className="tiny muted">{copy.dvoboj.briefHint}</p>
          <div className="nodelist" style={{ marginTop: 8 }}>
            {briefs.map((brief) => (
              <button
                key={brief.id}
                type="button"
                className={`noderow ${nodeId === brief.id ? 'chosen' : ''}`.trim()}
                onClick={() => {
                  sfx.move();
                  setNodeId(brief.id);
                }}
              >
                <span className="body">
                  <b>{brief.title}</b>
                  {brief.redoMine || brief.redoTheirs ? (
                    <span className="chip dim" style={{ marginLeft: 8 }}>{copy.dvoboj.redoTag}</span>
                  ) : null}
                </span>
              </button>
            ))}
          </div>
          {/*
            Said once, under the list, about whichever brief is selected. A redo is a perfectly good
            reason to duel — you already know what you got wrong last time — but it does not count
            twice, and finding that out afterwards would feel like the app had cheated.
          */}
          {chosen?.redoMine ? (
            <p className="tiny" style={{ marginTop: 8, color: 'var(--gold-dark)' }}>
              {copy.dvoboj.redoMine}
            </p>
          ) : null}
          {chosen?.redoTheirs && !chosen.redoMine ? (
            <p className="tiny muted" style={{ marginTop: 8 }}>{copy.dvoboj.redoTheirs(themName)}</p>
          ) : null}
        </>
      ) : null}

      <p className="eyebrow" style={{ marginTop: 14 }}>{copy.dvoboj.workLabel}</p>
      <div className="ose" style={{ marginTop: 6 }}>
        {ROKOVI_RADA.map((hours) => (
          <Btn
            key={hours}
            size="small"
            variant={workHours === hours ? 'primary' : undefined}
            onClick={() => setWorkHours(hours)}
          >
            {copy.dvoboj.rok(hours)}
          </Btn>
        ))}
      </div>

      <p className="eyebrow" style={{ marginTop: 14 }}>{copy.dvoboj.voteLabel}</p>
      <div className="ose" style={{ marginTop: 6 }}>
        {ROKOVI_GLASANJA.map((hours) => (
          <Btn
            key={hours}
            size="small"
            variant={voteHours === hours ? 'primary' : undefined}
            onClick={() => setVoteHours(hours)}
          >
            {copy.dvoboj.rok(hours)}
          </Btn>
        ))}
      </div>

      <div className="btn-row" style={{ marginTop: 16 }}>
        <Btn
          variant="primary"
          size="small"
          disabled={busy || !nodeId}
          onClick={() => onSubmit({ nodeId, workHours, voteHours })}
        >
          {submitLabel}
        </Btn>
        <Btn variant="ghost" size="small" onClick={onCancel}>
          {copy.dvoboj.back}
        </Btn>
      </div>
    </>
  );
}

/** „Izazovi na dvoboj" — opened from a person's card, on the map or in the crew. */
export function IzazovModal({
  memberId,
  islandId,
  onClose,
}: {
  memberId: string;
  islandId?: string | null;
  onClose: () => void;
}) {
  const [offer, setOffer] = useState<ChallengeOffer | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.dvoboj.offer(memberId).then(
      (result) => live && setOffer(result),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [memberId]);

  const send = async (terms: { nodeId: string; workHours: number; voteHours: number }) => {
    setBusy(true);
    setError(null);
    const result = await challenge({ memberId, ...terms, ...(islandId ? { islandId } : {}) });
    setBusy(false);
    if (!result.ok) {
      sfx.error();
      setError(result.message ?? null);
      return;
    }
    sfx.izazov();
    toast(copy.dvoboj.sent, copy.dvoboj.sentBody);
    onClose();
  };

  if (failed)
    return (
      <Modal title={copy.dvoboj.title} onClose={onClose}>
        <p className="muted">{copy.error.genericBody}</p>
      </Modal>
    );

  if (!offer)
    return (
      <Modal title={copy.dvoboj.title} onClose={onClose}>
        <p className="muted">…</p>
      </Modal>
    );

  // A duel already running between the two of them: open that one rather than starting another.
  if (offer.existing)
    return (
      <Modal title={copy.dvoboj.challengeWith(offer.name)} onClose={onClose}>
        <p style={{ fontSize: 14 }}>{copy.dvoboj.refusals['vec-traje']}</p>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Btn
            variant="primary"
            size="small"
            onClick={() => {
              onClose();
              void openDuel(offer.existing!);
            }}
          >
            {copy.dvoboj.title}
          </Btn>
        </div>
      </Modal>
    );

  if (offer.refusal)
    return (
      <Modal title={copy.dvoboj.challengeWith(offer.name)} onClose={onClose}>
        <p className="muted">{copy.dvoboj.refusals[offer.refusal] ?? copy.dvoboj.refusals['nema-zadatka']}</p>
      </Modal>
    );

  if (offer.briefs.length === 0)
    return (
      <Modal title={copy.dvoboj.challengeWith(offer.name)} onClose={onClose}>
        <p className="muted">{copy.dvoboj.noBriefs}</p>
      </Modal>
    );

  return (
    <Modal title={copy.dvoboj.challengeWith(offer.name)} onClose={onClose}>
      <p className="tiny muted">{copy.dvoboj.lead}</p>
      <Uslovi
        initial={{ nodeId: offer.briefs[0]!.id, workHours: 48, voteHours: 48 }}
        briefs={offer.briefs}
        themName={offer.name}
        busy={busy}
        submitLabel={copy.dvoboj.send}
        onCancel={onClose}
        onSubmit={(terms) => void send(terms)}
      />
      {error ? <p className="tiny" style={{ marginTop: 8, color: 'var(--warn)' }}>{error}</p> : null}
    </Modal>
  );
}

/* ================================================================ the strips */

/**
 * The SADA card, when a duel has a deadline on it.
 *
 * Not a second card beside the lesson — it replaces it. GDD §2① says one card, and a duel with six
 * hours left on it genuinely is the next step; the lesson has no deadline and will still be there.
 */
export function DvobojKarta({ duel }: { duel: Duel }) {
  const now = useNow();
  const me = useStore((s) => s.server?.member.id ?? '');
  const content = useStore(contentOf);
  const stage = stageOf(duel, now);
  const side = sideOf(duel, me);
  const them = side === null ? '' : nameOfSide(duel, other(side));
  const deadline = deadlineNow(duel, now);
  const title =
    content.islands.flatMap((i) => i.nodes).find((n) => n.id === duel.nodeId)?.title ?? duel.nodeId;

  const line =
    stage === 'poziv' || stage === 'izmena'
      ? turnOf(duel) === me
        ? copy.dvoboj.incomingFrom(them)
        : copy.dvoboj.waitingOnThem(them)
      : stage === 'ishod'
        ? copy.dvoboj.reviseWhy
        : stage === 'glasanje'
          ? copy.dvoboj.voting
          : (side !== null && submittedAt(duel, side) !== null
              ? copy.dvoboj.waitingForTheirs
              : copy.dvoboj.lead);

  return (
    <Win title={copy.dvoboj.title} tone={stage === 'ishod' ? 'gold' : undefined} className="dvoboj-karta">
      <div className="card-head">
        <span className="chip gold">{copy.dvoboj.challengeWith(them)}</span>
        {deadline ? <span className="chip">{shortLeft(deadline - now)}</span> : null}
      </div>
      <p className="title" style={{ marginTop: 8 }}>{title}</p>
      <p className="leads">{line}</p>
      <div className="btn-row" style={{ marginTop: 12 }}>
        <Btn variant="primary" onClick={() => void openDuel(duel.id)}>
          {copy.dvoboj.title}
        </Btn>
      </div>
    </Win>
  );
}

/**
 * A duel that is running but is not this member's move — waiting for an answer, for the other
 * work, or for the community.
 *
 * A strip rather than the card, because waiting is not a next step. It exists because the drafted
 * Skool post lives inside the arena, and a duel nobody can reach is a duel nobody brings voters to.
 */
export function DvobojStrip() {
  const now = useNow(30_000);
  const me = useStore((s) => s.server?.member.id ?? '');
  const duels = useStore(duelsOf);
  const waiting = waitingDuels(duels, me, now);
  if (waiting.length === 0) return null;
  const duel = waiting[0]!;
  const side = sideOf(duel, me) ?? 0;
  const stage = stageOf(duel, now);
  return (
    <button type="button" className="waiting-strip crew" onClick={() => void openDuel(duel.id)}>
      <b>{copy.dvoboj.challengeWith(nameOfSide(duel, other(side)))}</b>
      <span className="lead">
        {stage === 'glasanje' ? copy.dvoboj.voting : copy.dvoboj.waitingOnThem(nameOfSide(duel, other(side)))}
      </span>
    </button>
  );
}

/** Duels waiting for a vote. On the crew screen, because a vote is a crew action. */
export function GlasanjeStrip({ duels }: { duels: Duel[] }) {
  if (duels.length === 0) return null;
  const duel = duels[0]!;
  // No names. The strip used to print both duellists, which is the first half of the mapping the
  // ballot then carefully withholds — and „your friend is in this one" is exactly the nudge blind
  // voting exists to remove.
  return (
    <button type="button" className="waiting-strip crew" onClick={() => void openDuel(duel.id)}>
      <b>{copy.dvoboj.voteWaiting}</b>
      <span className="lead">{copy.dvoboj.voteBlind}</span>
    </button>
  );
}

